import { apiClient } from '../api/client';

export interface ChangelogItem {
  kind: string;
  target: string;
  note: string | null;
}

export interface ChangelogVersion {
  version: string;
  publishedAt: string | null;
  store: ChangelogItem[];
  core: { kind: 'fix' | 'feature'; text: string }[];
}

export type BuildTarget = 'win-x64' | 'linux-x64' | 'mac-x64' | 'mac-arm64' | 'android-arm64';

export interface DownloadEntry {
  target: BuildTarget;
  fileName: string;
  url: string;
  /** bigint como string — um instalador passa de 100 MB. */
  sizeBytes: string;
  sha256: string;
  /** Na v1 sempre `false` (ADR-05). A página explica o aviso do sistema. */
  isSigned: boolean;
}

export interface LauncherDownloads {
  appName: string;
  displayName: string;
  coreVersion: string | null;
  publishedAt: string | null;
  /** Vazio = a loja ainda não gerou nenhum instalador. Não é erro. */
  downloads: DownloadEntry[];
}

/**
 * Downloads públicos do launcher.
 *
 * Rota sem autenticação de propósito: quem baixa ainda não tem conta, e exigir
 * login para baixar o aplicativo onde se faz login seria circular.
 */
export const launcherService = {
  async getChangelog(
    storeId: string,
    version?: string,
  ): Promise<{ versions: ChangelogVersion[] }> {
    const query = version ? `?version=${encodeURIComponent(version)}` : '';

    return apiClient.get<{ versions: ChangelogVersion[] }>(
      `/launcher/${storeId}/changelog${query}`,
    );
  },

  async getDownloads(storeId: string): Promise<LauncherDownloads> {
    return apiClient.get<LauncherDownloads>(`/launcher/${storeId}/downloads`);
  },
};

/** Bytes → "104,8 MB". Chega como string porque o número não cabe em JSON. */
export function formatBytes(value: string | number): string {
  const bytes = Number(value);

  if (!Number.isFinite(bytes) || bytes <= 0) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const size = bytes / Math.pow(1024, exponent);

  return `${size.toFixed(exponent === 0 ? 0 : 1).replace('.', ',')} ${units[exponent]}`;
}
